import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { Sessao } from '../../auth';
import { ROTULO_DA_EXCECAO, hojeBrasilia, lerExcecoes, tratarExcecao } from '../../central';
import type { Excecao, Excecoes, TipoDeExcecao } from '../../central';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import { MODELOS_PARTES, fmt, partes } from '../../design/graficos/modelos';
import { reais } from '../../produtos';
import { hora } from '../../vendas';
import { BarraDeFiltros, Kpi, irPara, lerFiltros, textoDoPeriodo } from '../paineis/pecasDaVisao';
import { SubnavDeVendas } from './SubnavDeVendas';
import './central.css';
import { linhaClicavel } from '../../design/linha-clicavel';

/**
 * TELA 14A — EXCEÇÕES COMERCIAIS. "Sinaliza. Não acusa." Cada linha tem a conta explícita: o
 * valor observado, a base e a janela dessa base. Sem limiar configurado não existe severidade —
 * a tela diz "sem política" em vez de pintar de vermelho por conta própria. As três que dependem
 * de custo (abaixo do custo, margem negativa, desconto fora do padrão) usam o custo e o desconto
 * que a API do HOS manda por item — só existem nas vendas que vieram por ela (desde 12/09).
 */

const TIPOS: readonly TipoDeExcecao[] = [
  'abaixo_do_custo',
  'margem_negativa',
  'desconto_fora_do_padrao',
  'preco_abaixo_da_media',
  'queda_de_volume',
  'sem_correspondencia',
];
const EXPLICACAO: Record<TipoDeExcecao, string> = {
  abaixo_do_custo: 'item vendido por menos que o custo do dia',
  margem_negativa: 'produto que fechou o período no prejuízo',
  desconto_fora_do_padrao: 'desconto ≥ 20% e 15 pontos acima do normal do produto',
  preco_abaixo_da_media: 'lançamento abaixo da média de 30 dias',
  queda_de_volume: '7 dias contra 30 dias',
  sem_correspondencia: 'lançado e não achado nas vendas do HOS',
};
const STATUS: Record<Excecao['status'], string> = {
  nova: 'Nova',
  em_analise: 'Em análise',
  justificada: 'Justificada',
};
type FiltroDeStatus = 'abertas' | 'todas' | 'justificada';

const pct = (n: unknown) =>
  `${Number(n) > 0 ? '+' : Number(n) < 0 ? '−' : ''}${Math.abs(Number(n)).toFixed(1).replace('.', ',')}%`;
const umaCasa = (n: unknown) => Number(n).toFixed(1).replace('.', ',');
const diaCurto = (d: string) => d.split('-').reverse().slice(0, 2).join('/');
const primeiroNome = (s: string) => {
  const p = s.trim().split(/\s+/)[0] ?? s;
  return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
};

const ROTULO_DO_VALOR: Record<string, ((x: string | number | null) => string) | null> = {
  preco: (x) => `preço ${reais(String(x))}`,
  base: (x) => `média 30d ${reais(String(x))}`,
  amostra: (x) => `${String(x)} vendas na janela`,
  desvioPct: (x) => `desvio ${pct(x)}`,
  janelaDias: (x) => `janela ${String(x)} dias corridos`,
  quantidade: (x) => `${String(x)} un`,
  premioLancado: (x) => `prêmio lançado ${reais(String(x))}`,
  vendas: (x) => `${String(x)} venda(s)`,
  mediaRecente: (x) => `${umaCasa(x)}/dia nos 7 dias`,
  mediaAnterior: (x) => `${umaCasa(x)}/dia nos 30 dias`,
  variacaoPct: (x) => `variação ${pct(x)}`,
  janelaRecente: (x) => `janela recente ${String(x)} dias`,
  janelaAnterior: (x) => `base ${String(x)} dias`,
  cupom: (x) => `cupom ${String(x)}`,
  saleId: null,
  custo: (x) => `custo ${reais(String(x))}`,
  prejuizo: (x) => `prejuízo ${reais(String(x))}`,
  descontoPct: (x) => (x === null ? 'desconto não informado' : `desconto ${umaCasa(x)}%`),
  descontoMedioPct: (x) => `desconto médio do produto ${umaCasa(x)}%`,
  acimaPontos: (x) => `${umaCasa(x)} pontos acima`,
  faturamento: (x) => `faturamento ${reais(String(x))}`,
  margemPct: (x) => (x === null ? 'margem —' : `margem ${pct(x)}`),
  de: (x) => `de ${diaCurto(String(x))}`,
  ate: (x) => `até ${diaCurto(String(x))}`,
};
const valoresLegiveis = (v: Excecao['valores']) =>
  Object.entries(v)
    .map(([k, x]) => {
      const f = ROTULO_DO_VALOR[k];
      if (f === null) return null;
      return (f ?? ((y: string | number | null) => `${k} ${String(y)}`))(x);
    })
    .filter(Boolean)
    .join(' · ');

function valorDaLinha(e: Excecao): string {
  const v = e.valores;
  if (e.tipo === 'abaixo_do_custo') return reais(String(v['preco']));
  if (e.tipo === 'margem_negativa') return reais(String(v['faturamento']));
  if (e.tipo === 'desconto_fora_do_padrao') return `${umaCasa(v['descontoPct'])}%`;
  if (e.tipo === 'preco_abaixo_da_media') return reais(String(v['preco']));
  if (e.tipo === 'sem_correspondencia') return `${String(v['quantidade'])} un`;
  return '—';
}
function diferenca(e: Excecao): { valor: string; base: string } {
  const v = e.valores;
  if (e.tipo === 'abaixo_do_custo')
    return {
      valor: `−${reais(String(v['prejuizo']))}`,
      base: `custo do dia ${reais(String(v['custo']))} · ${String(v['quantidade'])} un`,
    };
  if (e.tipo === 'margem_negativa')
    return {
      valor: pct(v['margemPct']),
      base: `custo ${reais(String(v['custo']))} · ${String(v['vendas'])} vendas`,
    };
  if (e.tipo === 'desconto_fora_do_padrao')
    return {
      valor: `+${umaCasa(v['acimaPontos'])} pts`,
      base: `normal do produto ${umaCasa(v['descontoMedioPct'])}% · ${String(v['amostra'])} vendas`,
    };
  if (e.tipo === 'preco_abaixo_da_media')
    return {
      valor: pct(v['desvioPct']),
      base: `média 30d ${reais(String(v['base']))} · ${String(v['amostra'])} vendas`,
    };
  if (e.tipo === 'queda_de_volume')
    return {
      valor: pct(v['variacaoPct']),
      base: `${umaCasa(v['mediaRecente'])}/dia vs ${umaCasa(v['mediaAnterior'])}/dia em 30d`,
    };
  return {
    valor: reais(String(v['premioLancado'])),
    base: 'prêmio lançado sem cupom nas vendas do HOS',
  };
}

export function ExcecoesComerciais({
  sessao,
  parametros,
}: {
  sessao: Sessao;
  parametros: URLSearchParams;
}) {
  const hoje = hojeBrasilia();
  const q = new URLSearchParams(parametros);
  if (!q.get('p') && !q.get('dia')) q.set('p', 'semana');
  const f = lerFiltros(q, hoje);
  const periodo = f.periodo;
  const tipo = (
    TIPOS.includes(parametros.get('tipo') as TipoDeExcecao) ? parametros.get('tipo') : ''
  ) as TipoDeExcecao | '';
  const status = (
    ['todas', 'justificada'].includes(parametros.get('status') ?? '')
      ? parametros.get('status')
      : 'abertas'
  ) as FiltroDeStatus;
  const extras: { tipo: string | null; status: string | null } = {
    tipo: tipo || null,
    status: status === 'abertas' ? null : status,
  };
  const ir = (mudar: Partial<typeof extras>) =>
    irPara({ filtro: f.filtro, periodo: f.periodo, pessoa: null }, 'excecoes', {
      ...extras,
      ...mudar,
    });
  const [d, setD] = useState<Excecoes | null>(null);
  const [erro, setErro] = useState(false);
  const [aberta, setAberta] = useState<Excecao | null>(null);

  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerExcecoes(periodo.de, periodo.ate).then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, [periodo.de, periodo.ate]);
  useEffect(carregar, [carregar]);

  const todas = d?.excecoes ?? [];
  const abertas = todas.filter((e) => e.status !== 'justificada');
  // mais grave primeiro: pela ordem dos tipos; dentro do tipo, a ordem do servidor (maior desvio)
  const linhas = todas
    .filter(
      (e) =>
        (!tipo || e.tipo === tipo) &&
        (status === 'todas' ||
          (status === 'abertas' ? e.status !== 'justificada' : e.status === 'justificada')),
    )
    .map((e, k) => ({ e, k }))
    .sort((a, b) => TIPOS.indexOf(a.e.tipo) - TIPOS.indexOf(b.e.tipo) || a.k - b.k)
    .map((x) => x.e);
  const conta = (t: TipoDeExcecao) => abertas.filter((e) => e.tipo === t).length;
  const prejuizo = abertas
    .filter((e) => e.tipo === 'abaixo_do_custo')
    .reduce((s, e) => s + Number(e.valores['prejuizo'] ?? 0), 0);
  const porBalconista = new Map<string, number>();
  // o "Integrador" é o operador do sistema (vendas do site/app), não uma pessoa: fica na lista,
  // fora do gráfico de balconistas
  for (const e of abertas)
    if (e.balconista && !/^integrador$/i.test(e.balconista.trim()))
      porBalconista.set(e.balconista, (porBalconista.get(e.balconista) ?? 0) + 1);
  const quando = textoDoPeriodo(f.filtro, f.periodo, hoje);

  return (
    <section className="cv">
      <SubnavDeVendas atual="excecoes" excecoes={d ? abertas.length : undefined} />
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>Exceções comerciais</h1>
            <div className="vg-contexto">
              O que foge do padrão e pede um olhar — sinaliza, não acusa · {quando}
            </div>
          </div>
        </header>

        <BarraDeFiltros f={f} hoje={hoje} rota="excecoes" extras={extras} />

        {erro ? (
          <ErroDeArea oQue="as exceções" aoTentar={carregar} />
        ) : (
          <>
            <div className="vg-kpis cv-kpis-5">
              <Kpi
                rotulo={`Em aberto · ${quando}`}
                icone="warn"
                valor={d ? fmt(abertas.length, 'inteiro') : null}
                variacao={null}
                sub={
                  d
                    ? `${fmt(todas.filter((e) => e.status === 'nova').length, 'inteiro')} ainda sem análise`
                    : null
                }
                link="#/excecoes"
                destaque
              />
              <Kpi
                rotulo="Prejuízo abaixo do custo"
                icone="money"
                valor={d ? fmt(prejuizo, 'reais') : null}
                variacao={null}
                sub={
                  d
                    ? `${fmt(conta('abaixo_do_custo'), 'inteiro')} itens vendidos abaixo do custo`
                    : null
                }
                link="#/excecoes?tipo=abaixo_do_custo"
              />
              <Kpi
                rotulo="Margem negativa"
                icone="gauge"
                valor={d ? `${fmt(conta('margem_negativa'), 'inteiro')} produtos` : null}
                variacao={null}
                sub={d ? 'fecharam o período no prejuízo' : null}
                link="#/excecoes?tipo=margem_negativa"
              />
              <Kpi
                rotulo="Desconto fora do padrão"
                icone="receipt"
                valor={d ? fmt(conta('desconto_fora_do_padrao'), 'inteiro') : null}
                variacao={null}
                sub={d ? 'muito acima do normal do produto' : null}
                link="#/excecoes?tipo=desconto_fora_do_padrao"
              />
              <Kpi
                rotulo="Vendas abaixo da média"
                icone="arrow"
                valor={d ? `${fmt(conta('queda_de_volume'), 'inteiro')} produtos` : null}
                variacao={null}
                sub={d ? 'saindo menos que no mês' : null}
                link="#/excecoes?tipo=queda_de_volume"
              />
            </div>

            {d && abertas.length > 0 && (
              <div className="vg-linha cv-linha-2">
                <CartaoDeGrafico
                  id="cv-exc-tipo"
                  titulo="Exceções por tipo"
                  numero={`${fmt(abertas.length, 'inteiro')} em aberto`}
                  conclusao={(() => {
                    const maior = [...TIPOS].sort((a, b) => conta(b) - conta(a))[0];
                    return maior ? (
                      <>
                        O que mais aparece: <strong>{ROTULO_DA_EXCECAO[maior]}</strong> (
                        {fmt(conta(maior), 'inteiro')}). Clique no tipo abaixo para ver a lista.
                      </>
                    ) : null;
                  })()}
                  modelos={MODELOS_PARTES}
                  padrao="rosca"
                  desenhar={(m) =>
                    partes(m, {
                      itens: TIPOS.filter((t) => conta(t) > 0).map((t) => ({
                        nome: ROTULO_DA_EXCECAO[t],
                        valor: conta(t),
                      })),
                      formato: 'inteiro',
                    })
                  }
                  fonte="Fonte: vendas e custo da API do HOS · lançamentos do InovaPay"
                  altura={260}
                />
                <CartaoDeGrafico
                  id="cv-exc-balconista"
                  titulo="Exceções por balconista"
                  numero={(() => {
                    const top = [...porBalconista.entries()].sort((a, b) => b[1] - a[1])[0];
                    return top ? `${primeiroNome(top[0])} · ${fmt(top[1], 'inteiro')}` : '—';
                  })()}
                  conclusao={
                    porBalconista.size
                      ? 'Quem vendeu o item de cada exceção (sem o Integrador, que são as vendas do site). Conversa, não acusação: quem mais vende tende a aparecer mais.'
                      : 'Nenhuma exceção ligada a um balconista no período.'
                  }
                  modelos={MODELOS_PARTES}
                  padrao="barras"
                  desenhar={(m) =>
                    partes(m, {
                      itens: [...porBalconista.entries()]
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 10)
                        .map(([nome, valor]) => ({ nome: primeiroNome(nome), valor })),
                      formato: 'inteiro',
                    })
                  }
                  fonte="Fonte: vendas da API do HOS"
                  vazio={porBalconista.size ? null : 'Nenhuma exceção ligada a um balconista.'}
                  altura={260}
                />
              </div>
            )}

            <div className="cv-chips">
              <button
                type="button"
                className={tipo === '' ? 'cv-chip cv-chip-sel' : 'cv-chip'}
                onClick={() => ir({ tipo: null })}
              >
                Todas <b>{abertas.length}</b>
              </button>
              {TIPOS.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={tipo === t ? 'cv-chip cv-chip-sel' : 'cv-chip'}
                  title={EXPLICACAO[t]}
                  onClick={() => ir({ tipo: t })}
                >
                  {ROTULO_DA_EXCECAO[t]} <b>{conta(t)}</b>
                </button>
              ))}
              <label className="cv-select cv-select-solto">
                Status
                <select
                  value={status}
                  onChange={(e) =>
                    ir({ status: e.target.value === 'abertas' ? null : e.target.value })
                  }
                >
                  <option value="abertas">Nova, em análise</option>
                  <option value="justificada">Justificadas</option>
                  <option value="todas">Todas</option>
                </select>
              </label>
            </div>
            {tipo && <p className="cv-regra">Regra: {EXPLICACAO[tipo]}.</p>}

            <div className="vg-bloco cv-lista">
              {!d ? (
                <div className="vg-lista-osso" aria-busy="true">
                  {[0, 1, 2, 3, 4].map((k) => (
                    <i key={k} />
                  ))}
                </div>
              ) : linhas.length === 0 ? (
                <div className="cv-vazio cv-vazio-borda">
                  Nenhuma exceção {status === 'justificada' ? 'justificada' : 'em aberto'} no
                  período.
                  <span>Estado normal — sem celebração.</span>
                </div>
              ) : (
                <div className="cv-rolagem">
                  <table className="cv-tabela">
                    <thead>
                      <tr>
                        <th>Data / hora</th>
                        <th>Produto</th>
                        <th>Balconista</th>
                        <th>Tipo</th>
                        <th className="cv-dir">Valor</th>
                        <th className="cv-dir">Diferença · base</th>
                        <th>Severidade</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.map((e) => {
                        const df = diferenca(e);
                        return (
                          <tr
                            key={`${e.tipo}|${e.referencia}`}
                            className="cv-clicavel"
                            {...linhaClicavel(() => setAberta(e))}
                          >
                            <td className="cv-mono cv-cinza">
                              {diaCurto(e.dia)} · {e.em ? hora(e.em) : '—'}
                            </td>
                            <td>
                              <div className="cv-forte">{e.produto}</div>
                              <div className="cv-mono cv-cinza cv-peq">
                                {[
                                  e.codigo,
                                  e.valores['cupom'] ? `cupom ${String(e.valores['cupom'])}` : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ') || '—'}
                              </div>
                            </td>
                            <td>{e.balconista ?? <span className="cv-cinza">—</span>}</td>
                            <td>
                              <span className={`cv-etiqueta cv-etiqueta-${e.tipo}`}>
                                {ROTULO_DA_EXCECAO[e.tipo]}
                              </span>
                            </td>
                            <td className="cv-dir cv-forte">{valorDaLinha(e)}</td>
                            <td className="cv-dir">
                              <div
                                className={`cv-forte ${e.tipo === 'sem_correspondencia' ? '' : 'cv-neg'}`}
                              >
                                {df.valor}
                              </div>
                              <div className="cv-cinza cv-peq">{df.base}</div>
                            </td>
                            <td>
                              <span className="cv-sem-politica" title={d.limiarPorque}>
                                sem política
                              </span>
                            </td>
                            <td>
                              <span className={`cv-status cv-status-ex-${e.status}`}>
                                {STATUS[e.status]}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {d && (
                <div className="cv-paginacao">
                  <span>
                    {fmt(linhas.length, 'inteiro')} de {fmt(todas.length, 'inteiro')} exceções
                  </span>
                  <span>{d.desligadasPorque}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {aberta && d && (
        <GavetaDaExcecao
          e={aberta}
          periodo={periodo}
          sessao={sessao}
          limiarPorque={d.limiarPorque}
          aoFechar={() => setAberta(null)}
          aoSalvar={() => {
            setAberta(null);
            carregar();
          }}
        />
      )}
    </section>
  );
}

function Regua({
  linhas,
}: {
  linhas: readonly { rotulo: string; valor: number; texto: string; destaque?: boolean }[];
}) {
  const max = Math.max(...linhas.map((l) => l.valor), 0.01);
  return (
    <div className="cv-regua">
      {linhas.map((l) => (
        <div
          key={l.rotulo}
          className={l.destaque ? 'cv-regua-linha cv-regua-destaque' : 'cv-regua-linha'}
        >
          <span>{l.rotulo}</span>
          <div className="cv-regua-trilho">
            <i style={{ width: `${String((l.valor / max) * 100)}%` }} />
          </div>
          <strong>{l.texto}</strong>
        </div>
      ))}
    </div>
  );
}

function GavetaDaExcecao({
  e,
  periodo,
  sessao,
  limiarPorque,
  aoFechar,
  aoSalvar,
}: {
  e: Excecao;
  periodo: { de: string; ate: string };
  sessao: Sessao;
  limiarPorque: string;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [obs, setObs] = useState(e.observacao ?? '');
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  useEffect(() => {
    const esc = (k: KeyboardEvent) => k.key === 'Escape' && aoFechar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);

  const salvar = (status: 'em_analise' | 'justificada') => {
    if (status === 'justificada' && !obs.trim()) {
      setFalha('Para justificar, escreva o contexto na observação.');
      return;
    }
    setEnviando(true);
    setFalha(null);
    void tratarExcecao(sessao, {
      tipo: e.tipo,
      referencia: e.referencia,
      de: periodo.de,
      ate: periodo.ate,
      status,
      observacao: obs.trim() || null,
    }).then((r) => {
      setEnviando(false);
      if (r.ok) aoSalvar();
      else setFalha(r.mensagem);
    });
  };

  const v = e.valores;
  let titulo = '';
  let regua: ReactNode = null;
  let resultado: { rotulo: string; valor: string; sub: string }[] = [];
  let observou = '';
  if (e.tipo === 'abaixo_do_custo') {
    titulo = `Vendido ${reais(String(v['prejuizo']))} abaixo do custo`;
    regua = (
      <Regua
        linhas={[
          { rotulo: 'Custo do dia', valor: Number(v['custo']), texto: reais(String(v['custo'])) },
          {
            rotulo: 'Preço vendido',
            valor: Number(v['preco']),
            texto: reais(String(v['preco'])),
            destaque: true,
          },
        ]}
      />
    );
    resultado = [
      {
        rotulo: 'PREJUÍZO',
        valor: reais(String(v['prejuizo'])),
        sub: `${String(v['quantidade'])} un × (custo − preço)`,
      },
      {
        rotulo: 'DESCONTO DO ITEM',
        valor: v['descontoPct'] === null ? '—' : `${umaCasa(v['descontoPct'])}%`,
        sub: 'como o HOS registrou',
      },
    ];
    observou =
      'o item saiu por menos que o custo que o HOS tinha para ele no dia da venda. Pode ser promoção combinada, preço errado no cadastro ou desconto dado no caixa — a exceção é para análise, não conclusão.';
  } else if (e.tipo === 'margem_negativa') {
    titulo = `Margem ${pct(v['margemPct'])} no período`;
    regua = (
      <Regua
        linhas={[
          { rotulo: 'Custo', valor: Number(v['custo']), texto: reais(String(v['custo'])) },
          {
            rotulo: 'Faturamento',
            valor: Number(v['faturamento']),
            texto: reais(String(v['faturamento'])),
            destaque: true,
          },
        ]}
      />
    );
    resultado = [
      { rotulo: 'PREJUÍZO', valor: reais(String(v['prejuizo'])), sub: 'custo − faturamento' },
      { rotulo: 'VENDAS', valor: String(v['vendas']), sub: 'itens com custo no período' },
    ];
    observou =
      'somando as vendas do produto no período, o custo do dia de cada venda passou do que entrou. Vale olhar o preço de venda no cadastro e o custo da última compra.';
  } else if (e.tipo === 'desconto_fora_do_padrao') {
    titulo = `Desconto de ${umaCasa(v['descontoPct'])}% — o normal é ${umaCasa(v['descontoMedioPct'])}%`;
    regua = (
      <Regua
        linhas={[
          {
            rotulo: 'Normal do produto',
            valor: Number(v['descontoMedioPct']),
            texto: `${umaCasa(v['descontoMedioPct'])}%`,
          },
          {
            rotulo: 'Esta venda',
            valor: Number(v['descontoPct']),
            texto: `${umaCasa(v['descontoPct'])}%`,
            destaque: true,
          },
        ]}
      />
    );
    resultado = [
      {
        rotulo: 'ACIMA DO NORMAL',
        valor: `${umaCasa(v['acimaPontos'])} pts`,
        sub: 'pontos percentuais',
      },
      {
        rotulo: 'PREÇO',
        valor: reais(String(v['preco'])),
        sub: `base: ${String(v['amostra'])} vendas em 30 dias`,
      },
    ];
    observou =
      'o desconto deste item ficou muito acima do que o mesmo produto costuma ter (≥ 20% e 15 pontos acima da média dos 30 dias anteriores). Pode ser convênio, PBM ou negociação — ou desconto demais.';
  } else if (e.tipo === 'preco_abaixo_da_media') {
    titulo = `Preço ${Math.abs(Number(v['desvioPct'])).toFixed(1).replace('.', ',')}% abaixo da média de 30 dias`;
    regua = (
      <Regua
        linhas={[
          { rotulo: 'Média 30 dias', valor: Number(v['base']), texto: reais(String(v['base'])) },
          {
            rotulo: 'Venda analisada',
            valor: Number(v['preco']),
            texto: reais(String(v['preco'])),
            destaque: true,
          },
        ]}
      />
    );
    resultado = [
      { rotulo: 'DESVIO DE PREÇO', valor: pct(v['desvioPct']), sub: 'contra a média de 30 dias' },
      { rotulo: 'AMOSTRA', valor: String(v['amostra']), sub: 'vendas lançadas na janela' },
    ];
    observou =
      'o preço lançado ficou abaixo da média das vendas do mesmo produto nos 30 dias anteriores. Isso pode ser promoção, faixa diferente ou cadastro — a exceção é registrada para análise, não como conclusão.';
  } else if (e.tipo === 'queda_de_volume') {
    titulo = `Vendas ${Math.abs(Number(v['variacaoPct'])).toFixed(1).replace('.', ',')}% abaixo da média`;
    regua = (
      <Regua
        linhas={[
          {
            rotulo: 'Média 30 dias',
            valor: Number(v['mediaAnterior']),
            texto: `${umaCasa(v['mediaAnterior'])}/dia`,
          },
          {
            rotulo: 'Últimos 7 dias',
            valor: Number(v['mediaRecente']),
            texto: `${umaCasa(v['mediaRecente'])}/dia`,
            destaque: true,
          },
        ]}
      />
    );
    resultado = [
      {
        rotulo: 'VARIAÇÃO DE VOLUME',
        valor: pct(v['variacaoPct']),
        sub: '7 dias contra os 30 anteriores',
      },
    ];
    observou =
      'o produto saiu menos por dia na última semana do que no mês anterior, pelas vendas do HOS. Pode ser falta de estoque, sazonalidade ou mudança de preço.';
  } else {
    titulo = 'Item lançado sem correspondência no relatório';
    resultado = [
      {
        rotulo: 'QUANTIDADE LANÇADA',
        valor: `${String(v['quantidade'])} un`,
        sub: `${String(v['vendas'])} lançamento(s)`,
      },
      {
        rotulo: 'PRÊMIO LANÇADO',
        valor: reais(String(v['premioLancado'])),
        sub: 'fora do cheque enquanto não bater',
      },
    ];
    observou =
      'o balconista lançou esta venda no InovaPay e a venda do HOS do mesmo dia não tem o produto no cupom dele. É a mesma leitura da Conferência de vendas.';
  }

  return (
    <div className="cv-veu" onClick={aoFechar}>
      <aside
        className="cv-gaveta"
        role="dialog"
        aria-label="Detalhe da exceção"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header>
          <div className="cv-gaveta-selos">
            <span className="cv-sem-politica">sem política</span>
            <span className={`cv-status cv-status-ex-${e.status}`}>
              {STATUS[e.status].toUpperCase()}
            </span>
            {e.congelada && (
              <span className="cv-cinza cv-peq">valores congelados na abertura do caso</span>
            )}
          </div>
          <button type="button" className="cv-fechar" aria-label="Fechar" onClick={aoFechar}>
            <Icone nome="x" tamanho={15} traco={1.8} />
          </button>
          <h2>{titulo}</h2>
          <span className={`cv-etiqueta cv-etiqueta-${e.tipo}`}>{ROTULO_DA_EXCECAO[e.tipo]}</span>
          {typeof e.valores['saleId'] === 'string' && (
            <a className="cv-abrir-cupom" href={`#/cupom?id=${e.valores['saleId']}`}>
              <Icone nome="receipt" tamanho={13} traco={1.7} />
              Abrir cupom {String(e.valores['cupom'] ?? '')}
            </a>
          )}
          <p>
            {e.produto}
            {e.codigo ? ` · ${e.codigo}` : ''} · {e.dia.split('-').reverse().join('/')}
            {e.em ? ` às ${hora(e.em)}` : ''}
            {e.balconista ? ` · ${e.balconista}` : ''}
          </p>
        </header>
        <div className="cv-gaveta-corpo">
          {regua && (
            <>
              <h3>Comparação</h3>
              {regua}
            </>
          )}
          <div className="cv-resultados">
            {resultado.map((r, k) => (
              <div
                key={r.rotulo}
                className={k === 0 ? 'cv-resultado cv-resultado-forte' : 'cv-resultado'}
              >
                <span>{r.rotulo}</span>
                <strong>{r.valor}</strong>
                <em>{r.sub}</em>
              </div>
            ))}
          </div>
          <div className="cv-nota">
            <p>
              <strong>O que a regra observou:</strong> {observou}
            </p>
            <p className="cv-peq">
              <strong>Severidade:</strong> {limiarPorque}
            </p>
            <p className="cv-peq cv-cinza">
              <strong>Valores usados na detecção</strong>{' '}
              {e.congelada
                ? '(guardados com o caso, não recalculados depois)'
                : '(calculados agora; ficam guardados quando o caso for aberto)'}
              : {valoresLegiveis(v)}
            </p>
          </div>
          <h3>Observação do gestor</h3>
          <textarea
            value={obs}
            onChange={(ev) => setObs(ev.target.value)}
            maxLength={1000}
            rows={4}
            placeholder="Registre o contexto, se houver…"
          />
          {falha && (
            <p className="cv-falha" role="alert">
              {falha}
            </p>
          )}
        </div>
        <footer>
          <button
            type="button"
            className="cv-botao-primario"
            disabled={enviando}
            onClick={() => salvar('em_analise')}
          >
            Salvar e marcar em análise
          </button>
          <button
            type="button"
            className="cv-botao"
            disabled={enviando}
            onClick={() => salvar('justificada')}
          >
            Marcar como justificado
          </button>
        </footer>
      </aside>
    </div>
  );
}
